import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { analytics } from "./analytics";
import { SESSION_EXPIRED_EVENT, clearAppStorage, getStoredTokens, setStoredTokens } from "./session";
import type { Tokens } from "../types/domain";

interface AuthContextValue {
  tokens: Tokens | null;
  isAuthenticated: boolean;
  isSuperadmin: boolean;
  isConsultant: boolean;
  saveSession: (value: Tokens) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [tokens, setTokens] = useState<Tokens | null>(() => getStoredTokens());

  useEffect(() => {
    function handleSessionExpired() {
      setTokens(null);
      clearAppStorage();
      navigate("/auth", { replace: true });
    }

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [navigate]);

  const value = useMemo<AuthContextValue>(
    () => ({
      tokens,
      isAuthenticated: Boolean(tokens?.access_token),
      isSuperadmin: Boolean(tokens?.is_superadmin),
      isConsultant: Boolean(tokens?.is_consultant),
      saveSession: (next: Tokens) => {
        setTokens(next);
        setStoredTokens(next);
        try {
          const payload = JSON.parse(atob(next.access_token.split(".")[1]));
          if (payload.sub) {
            analytics.identify(payload.sub, {
              email: payload.email,
              role: next.role,
              workspace_id: next.workspace_id,
            });
          }
        } catch {
          // JWT decode failed — skip analytics identify
        }
      },
      logout: () => {
        analytics.reset();
        setTokens(null);
        clearAppStorage();
        navigate("/auth", { replace: true });
      }
    }),
    [navigate, tokens]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve essere usato dentro AuthProvider");
  }
  return ctx;
}
